import type { IOpenIDCredentials } from "matrix-widget-api";

const GAME_SERVER = import.meta.env.VITE_GAME_SERVER || "https://coho.mabus.ai";

export interface MatrixAuthResponse {
  token: string;
  player: {
    id: string;
    username: string;
    race: string;
  };
  isNewPlayer: boolean;
}

/**
 * Authenticate with the Cosmic Horizon server using a Matrix OpenID token.
 * The server validates the token with the Matrix homeserver, then creates
 * or finds the player account linked to that Matrix user.
 */
export async function authenticateWithMatrix(
  openIdToken: IOpenIDCredentials,
  matrixUserId?: string,
): Promise<MatrixAuthResponse> {
  const body: Record<string, unknown> = {
    openIdToken: {
      access_token: openIdToken.access_token,
      token_type: openIdToken.token_type,
      matrix_server_name: openIdToken.matrix_server_name,
      expires_in: openIdToken.expires_in,
    },
  };
  // Send userId if available (server derives it from token if not provided)
  if (matrixUserId) {
    body.matrixUserId = matrixUserId;
  }

  const res = await fetch(`${GAME_SERVER}/api/auth/matrix`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Auth failed (${res.status})`);
  }

  return res.json();
}

/** Build the game client URL with the auth token embedded */
export function buildGameUrl(token: string): string {
  const base = import.meta.env.VITE_GAME_CLIENT || GAME_SERVER;
  return `${base}?matrixToken=${encodeURIComponent(token)}`;
}
